// src/services/scribeRecordingService.js
// Browser-based audio recording service for PrismScribe web app
// Integrates with scribeEncounterService for audio handling

import { scribeEncounterService } from './scribeEncounterService';

class ScribeRecordingService {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.isRecording = false;
    this.isPaused = false;
    this.recordingStartTime = null;
    this.statusCallback = null;
    this.statusInterval = null;
    this.selectedDeviceId = null;
    this.availableDevices = [];
    this.wakeLock = null;
    this.noSleepVideo = null;

    // Single long-lived recorder state. Native pause()/resume() keeps ONE
    // MediaRecorder alive across pauses, so audioChunks always concatenate
    // into a valid single-container file (only the first chunk carries the
    // container header). A forced restart (dead track, unsupported pause)
    // starts a NEW session whose audio becomes a separate segment file —
    // blobs from different recorder sessions are never concatenated.
    this.stopRequested = false;
    this.needsNewRecorderOnResume = false;
    this.resumeInProgress = false;
    this.pendingStopResolve = null;
    // Set synchronously on entry to startRecording so taps that land while
    // the microphone is still being acquired cannot start a second recorder.
    this.isStarting = false;

    this.setupWakeLockVisibilityHandler();
  }

  /**
   * Request a wake lock to prevent screen from sleeping
   * Uses Wake Lock API on modern browsers, falls back to video trick on older iOS
   */
  async requestWakeLock() {
    // Try native Wake Lock API first (iOS 16.4+, Chrome 84+)
    if ('wakeLock' in navigator) {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen');
        
        this.wakeLock.addEventListener('release', () => {
        });
        
        return true;
      } catch (err) {
        console.warn('[RecordingService] Native wake lock failed:', err.message);
        // Fall through to video fallback
      }
    }

    // Fallback: NoSleep video trick for older iOS
    // Playing a video prevents the screen from sleeping
    try {
      await this.enableNoSleepVideo();
      return true;
    } catch (err) {
      console.warn('[RecordingService] Video fallback failed:', err.message);
      return false;
    }
  }

  /**
   * NoSleep video fallback - works on older iOS by playing a silent video
   * iOS won't sleep while media is playing
   */
  async enableNoSleepVideo() {
    if (this.noSleepVideo) return; // Already enabled

    // Create a tiny video element with a data URI (small transparent webm)
    // This is the NoSleep.js approach
    this.noSleepVideo = document.createElement('video');
    this.noSleepVideo.setAttribute('playsinline', '');
    this.noSleepVideo.setAttribute('muted', '');
    this.noSleepVideo.setAttribute('loop', '');
    this.noSleepVideo.style.position = 'absolute';
    this.noSleepVideo.style.left = '-9999px';
    this.noSleepVideo.style.width = '1px';
    this.noSleepVideo.style.height = '1px';

    // Minimal MP4 video (works better on iOS than webm)
    // This is a tiny silent video that loops
    const base64Video = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAs1tZGF0AAACrgYF//+q3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE0OCByMjYwMSBhMGNkN2QzIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNSAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTI1IHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAABBZYiEAD//8m+P5/THQT/BRgAAAwBgG+JB6AAEeQAAAAMAAQAAAwAAAwAAAwAAAwCiAAAADAAAHuEAAEGOAAAADAAAO4oAAD6AAAAAwAAZHAAAAwAAIYAAAAMAACkQAAADAAAqkAAAAwAAK5AAAAMAACwQAAADAAAswAAAAwAALWAAAAMAAC4AAAADAAA==';

    this.noSleepVideo.src = base64Video;
    document.body.appendChild(this.noSleepVideo);

    // Must be triggered by user interaction, but we're in startRecording which is user-initiated
    await this.noSleepVideo.play();
  }

  /**
   * Disable the NoSleep video
   */
  disableNoSleepVideo() {
    if (this.noSleepVideo) {
      this.noSleepVideo.pause();
      this.noSleepVideo.src = '';
      if (this.noSleepVideo.parentNode) {
        this.noSleepVideo.parentNode.removeChild(this.noSleepVideo);
      }
      this.noSleepVideo = null;
    }
  }

  /**
   * Release the wake lock (both native and fallback)
   */
  async releaseWakeLock() {
    // Release native wake lock
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
      } catch (err) {
        console.warn('[RecordingService] Wake lock release failed:', err.message);
      }
    }
    
    // Disable video fallback
    this.disableNoSleepVideo();
  }

  /**
   * Check if wake lock is currently active
   */
  isWakeLockActive() {
    return (this.wakeLock !== null && !this.wakeLock.released) || this.noSleepVideo !== null;
  }

  /**
   * Handle visibility change - re-acquire wake lock when page becomes visible
   * This is needed because iOS releases wake lock when tab loses visibility
   */
  setupWakeLockVisibilityHandler() {
    if (typeof document === 'undefined') return; // Guard for SSR
    
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible' && this.isRecording) {
        // Re-acquire wake lock when page becomes visible again
        await this.requestWakeLock();
      }
    });
  }

  /**
   * Get list of available audio input devices
   * Call this to populate a device selector UI
   */
  async getAudioInputDevices() {
    try {
      // Need to request permission first to get device labels
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableDevices = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          isDefault: device.deviceId === 'default'
        }));
      
      // Stop the temp stream
      tempStream.getTracks().forEach(track => track.stop());
      
      return this.availableDevices;
    } catch (error) {
      console.error('[RecordingService] Error getting audio devices:', error);
      return [];
    }
  }

  /**
   * Set the audio input device to use
   * @param {string|null} deviceId - Device ID or null for system default
   */
  setAudioInputDevice(deviceId) {
    this.selectedDeviceId = deviceId;
  }

  /**
   * Get supported MIME type for current browser
   */
  getSupportedMimeType() {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'audio/webm';
  }

  /**
   * Create a new MediaRecorder on the current stream and start it.
   * Reused by startRecording and the forced-restart resume path.
   * NOTE: audioChunks is NOT cleared here — chunks must survive native
   * pause/resume. Callers starting a new recorder SESSION clear it.
   */
  createAndStartRecorder() {
    const mimeType = this.getSupportedMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      audioBitsPerSecond: 64000
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
      if (this._flushWaiterResolve) {
        this._flushWaiterResolve();
        this._flushWaiterResolve = null;
      }
    };

    this.mediaRecorder.onstop = async () => {
      await this.handleRecordingStopped();
    };

    this.mediaRecorder.onerror = (event) => {
      console.error('[RecordingService] MediaRecorder error:', event);
      // Treated as an unexpected stop — onstop fires next and preserves the
      // session as paused (forced-restart on resume). No cleanup here.
    };

    this.mediaRecorder.start(1000);
  }

  /**
   * Ask the recorder to flush buffered audio and wait briefly for the chunk.
   * Bounded wait — with a 1000ms timeslice, any tail loss is under a second.
   */
  async flushRecorderData() {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') return;

    try {
      const waiter = new Promise(resolve => {
        this._flushWaiterResolve = resolve;
        setTimeout(() => {
          if (this._flushWaiterResolve) {
            this._flushWaiterResolve = null;
            resolve();
          }
        }, 500);
      });
      this.mediaRecorder.requestData();
      await waiter;
    } catch (err) {
      console.warn('[RecordingService] requestData flush failed:', err.message);
      this._flushWaiterResolve = null;
    }
  }

  /**
   * Acquire a mic stream, handling permission errors and device constraints.
   */
  async acquireStream() {
    const audioConstraints = {
      audio: this.selectedDeviceId
        ? { deviceId: { exact: this.selectedDeviceId } }
        : true
    };

    this.stream = await navigator.mediaDevices.getUserMedia(audioConstraints)
      .catch(err => {
        if (err.name === 'NotAllowedError') {
          throw new Error('Microphone permission denied. Please allow microphone access and try again.');
        } else if (err.name === 'NotFoundError') {
          throw new Error('No microphone found. Please check your audio input device.');
        } else if (err.name === 'OverconstrainedError') {
          console.warn('[RecordingService] Device constraint failed, trying default');
          return navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          throw err;
        }
      });
  }

  /**
   * Start recording
   * @param {Function} onStatusUpdate - Callback for status updates (duration, etc.)
   */
  async startRecording(onStatusUpdate) {
    if (this.isRecording || this.isStarting) {
      return { success: false, error: 'Already recording' };
    }
    this.isStarting = true;

    try {
      const prepResult = scribeEncounterService.prepareRecording();

      await this.acquireStream();

      this.isRecording = true;
      this.isPaused = false;
      this.recordingStartTime = Date.now();
      this.statusCallback = onStatusUpdate;

      this.audioChunks = [];
      this.needsNewRecorderOnResume = false;
      this.stopRequested = false;
      this.createAndStartRecorder();
      this.startStatusUpdates();

      const wakeLockAcquired = await this.requestWakeLock();

      return {
        success: true,
        recordingId: prepResult.recordingId,
        isAppending: prepResult.isAppending,
        segmentCount: prepResult.segmentCount,
        wakeLockActive: wakeLockAcquired
      };
    } catch (error) {
      console.error('[RecordingService] Failed to start recording:', error);
      this.cleanup();
      return {
        success: false,
        error: error.message || 'Failed to access microphone'
      };
    } finally {
      this.isStarting = false;
    }
  }

  /**
   * Build the current session's cumulative blob from accumulated chunks.
   * Chunks come from ONE recorder session, so this is always a valid
   * single-container file.
   */
  buildSessionBlob() {
    const mimeType = this.mediaRecorder?.mimeType || this.getSupportedMimeType() || 'audio/webm';
    return new Blob(this.audioChunks, { type: mimeType });
  }

  /**
   * Stop recording
   */
  async stopRecording() {
    if (!this.isRecording) {
      return { success: false, error: 'Not recording' };
    }

    // Live recorder (recording or natively paused): a real stop finalizes it.
    if (this.mediaRecorder && (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused')) {
      if (this.mediaRecorder.state === 'recording') {
        await this.flushRecorderData();
      }

      return new Promise((resolve) => {
        this.pendingStopResolve = resolve;
        this.stopRequested = true;
        this.mediaRecorder.stop();
      });
    }

    // Paused with a dead/spent recorder (forced-restart state): chunks were
    // already persisted (or are dirty and need a retry) — save and clean up.
    let success = true;
    let error;

    try {
      if (scribeEncounterService.currentRecording?.dirty) {
        const result = await scribeEncounterService.persistSessionAudio(
          this.buildSessionBlob(),
          { closeTimingSegment: false }
        );
        success = result.success;
        error = result.error;
      } else {
        // Metadata-only save persists final duration/segments.
        await scribeEncounterService.runSave();
      }
    } catch (e) {
      success = false;
      error = e.message;
    }

    const totalDuration = scribeEncounterService.getTotalRecordingDuration();
    this.cleanup();

    return { success, totalDuration, canResume: true, error };
  }

  /**
   * Handle recording stopped - called by MediaRecorder onstop.
   * A requested stop persists and resolves the stop promise; an UNEXPECTED
   * stop (iOS killed the recorder) is treated as a forced pause — the session
   * survives, and resume starts a new recorder session (new segment file).
   */
  async handleRecordingStopped() {
    const wasRequested = this.stopRequested;
    this.stopRequested = false;

    try {
      const audioBlob = this.buildSessionBlob();
      const mimeType = audioBlob.type;
      const duration = Date.now() - this.recordingStartTime;

      if (!wasRequested) {
        console.warn('[RecordingService] Unexpected recorder stop — preserving session as paused');
        this.isPaused = true;
        this.needsNewRecorderOnResume = true;
        this.mediaRecorder = null;

        if (this.audioChunks.length > 0) {
          await scribeEncounterService.persistSessionAudio(audioBlob, { closeTimingSegment: true });
        }
        // No cleanup — keep stream/wake lock/interval, isRecording stays true
        // so the paused UI and cross-note guard remain correct.
        return;
      }

      const result = await scribeEncounterService.persistSessionAudio(audioBlob, { closeTimingSegment: true });

      this.cleanup();

      if (this.pendingStopResolve) {
        this.pendingStopResolve({
          success: result.success,
          audioBlob: audioBlob,
          duration: duration,
          mimeType: mimeType,
          totalDuration: result.totalDuration,
          canResume: result.canResume,
          error: result.error
        });
        this.pendingStopResolve = null;
      }
    } catch (error) {
      console.error('[RecordingService] Error handling recording stopped:', error);

      if (!wasRequested) {
        this.isPaused = true;
        this.needsNewRecorderOnResume = true;
        this.mediaRecorder = null;
        return;
      }

      this.cleanup();

      if (this.pendingStopResolve) {
        this.pendingStopResolve({
          success: false,
          error: error.message
        });
        this.pendingStopResolve = null;
      }
    }
  }

  /**
   * Pause recording — native MediaRecorder.pause() keeps the recorder session
   * alive (chunks keep concatenating validly), then the accumulated chunks are
   * snapshotted into a valid single-container blob and uploaded. Durability:
   * this resolves only after the upload settles, so after every successful
   * pause the audio is safe on the server.
   */
  async pauseRecording() {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording' || this.isPaused) {
      return { success: false, error: 'Not recording' };
    }

    this.isPaused = true;

    await this.flushRecorderData();

    try {
      this.mediaRecorder.pause();
    } catch (err) {
      // pause() unsupported or recorder in a bad state: degrade to a forced
      // restart — stop this recorder (onstop persists via the unexpected-stop
      // path) and let resume start a new session. NEVER concatenates blobs.
      console.warn('[RecordingService] Native pause failed, degrading to session restart:', err.message);
      this.needsNewRecorderOnResume = true;
      try {
        this.mediaRecorder.stop();
      } catch {
        this.mediaRecorder = null;
      }
    }

    return await scribeEncounterService.persistSessionAudio(
      this.buildSessionBlob(),
      { closeTimingSegment: true }
    );
  }

  /**
   * Resume recording after a pause. Happy path: native resume() on the same
   * recorder — chunks keep accumulating in the same session. Fallback (dead
   * track, spent recorder): re-acquire the mic and start a NEW recorder
   * session whose audio becomes a separate segment file.
   */
  async resumeRecording() {
    if (!this.isRecording || !this.isPaused) {
      return { success: false, error: 'Not paused' };
    }
    if (this.resumeInProgress) {
      return { success: false, error: 'Resume in progress' };
    }

    this.resumeInProgress = true;
    try {
      const track = this.stream?.getAudioTracks()[0];
      const trackAlive = this.stream && track && track.readyState !== 'ended';
      const recorderPaused = this.mediaRecorder?.state === 'paused';

      if (trackAlive && recorderPaused && !this.needsNewRecorderOnResume) {
        // Native resume — same session, same eventual file slot.
        scribeEncounterService.openTimingSegment();
        this.mediaRecorder.resume();
        this.isPaused = false;
        return { success: true };
      }

      // Forced restart: new stream if needed, new recorder session → the new
      // session's audio uploads as an appended segment file.
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.stopRequested = false;
        try { this.mediaRecorder.stop(); } catch { /* spent */ }
      }

      // Previous session's audio must be safe before its chunks are dropped.
      if (scribeEncounterService.currentRecording?.dirty) {
        try {
          await scribeEncounterService.runSave();
        } catch { /* surfaced below */ }
      }

      if (!trackAlive) {
        await this.acquireStream();
      }

      scribeEncounterService.beginNewRecorderSession();
      scribeEncounterService.openTimingSegment();

      this.audioChunks = [];
      this.recordingStartTime = Date.now();
      this.createAndStartRecorder();
      this.needsNewRecorderOnResume = false;
      this.isPaused = false;

      return { success: true };
    } catch (err) {
      console.error('[RecordingService] Resume failed:', err);
      return { success: false, error: err.message };
    } finally {
      this.resumeInProgress = false;
    }
  }

  /**
   * Get current recording status
   */
  getStatus() {
    return {
      isRecording: this.isRecording,
      isPaused: this.isPaused,
      duration: this.getCurrentDuration(),
      state: this.mediaRecorder?.state || 'inactive'
    };
  }

  /**
   * Get current recording duration — cumulative across all segments.
   */
  getCurrentDuration() {
    if (!this.isRecording) return 0;
    return scribeEncounterService.getTotalRecordingDuration();
  }

  /**
   * Start sending status updates
   */
  startStatusUpdates() {
    this.statusInterval = setInterval(() => {
      if (this.isRecording && this.statusCallback) {
        this.statusCallback({
          duration: this.getCurrentDuration(),
          state: this.mediaRecorder?.state,
          isPaused: this.isPaused
        });
      }
    }, 100);
  }

  /**
   * Stop status updates
   */
  stopStatusUpdates() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stopStatusUpdates();
    this.releaseWakeLock();

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
    this.isPaused = false;
    this.recordingStartTime = null;
    this.statusCallback = null;

    this.stopRequested = false;
    this.needsNewRecorderOnResume = false;
    this.resumeInProgress = false;
    this.isStarting = false;
    this._flushWaiterResolve = null;
  }

  /**
   * Check if browser supports recording
   */
  static isSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
  }

  /**
   * Get info about the currently active audio track (if recording)
   */
  getActiveTrackInfo() {
    if (!this.stream) return null;
    
    const audioTrack = this.stream.getAudioTracks()[0];
    if (!audioTrack) return null;
    
    const settings = audioTrack.getSettings();
    return {
      label: audioTrack.label,
      deviceId: settings.deviceId,
      channelCount: settings.channelCount,
      sampleRate: settings.sampleRate
    };
  }
}

export const scribeRecordingService = new ScribeRecordingService();